// ══════════════════════════════════════════════════════════════════════
// GQ Optimisation library
// • Genetic algorithm (single + multi-objective NSGA-II)
// • Bayesian search (Gaussian-process surrogate w/ EI acquisition)
// • Grid + random baselines
// • Walk-forward / nested CV / CPCV
// • PBO (Probability of Backtest Overfitting)
// • Sensitivity grids + neighbour-robustness scores
//
// Param-range spec:
//   ranges = { rsi_period:[5,30,'int'], stop:[0.01,0.05,'float'],
//              direction:['long','short'] }
//
// Fitness function receives a paramset, returns either a number (single-obj)
// or {sharpe, maxDD, ...} (multi-obj). Library is generic — caller wires
// the actual backtest call.
// ══════════════════════════════════════════════════════════════════════

(function (root) {
  const stats = (root && root.GQ && root.GQ.stats)
    || (typeof require !== 'undefined' ? require('./stats.js') : null);

  // Deterministic RNG (xorshift32)
  function rng(seed = 1) {
    let s = seed >>> 0;
    return () => {
      s ^= s << 13; s ^= s >>> 17; s ^= s << 5;
      return ((s >>> 0) % 1_000_000) / 1_000_000;
    };
  }
  function gauss(rng) {
    return () => {
      let u = 0, v = 0;
      while (u === 0) u = rng();
      while (v === 0) v = rng();
      return Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * v);
    };
  }

  // ────────────────────────────────────────────────────────────────────
  // Param sampling
  // ────────────────────────────────────────────────────────────────────
  function sampleParam(spec, _) {
    if (Array.isArray(spec) && typeof spec[0] === 'string') {
      // categorical
      return spec[Math.floor(_() * spec.length)];
    }
    const [lo, hi, type = 'float'] = spec;
    if (type === 'int') return Math.floor(lo + _() * (hi - lo + 1));
    return lo + _() * (hi - lo);
  }
  function sampleIndividual(ranges, _) {
    const ind = {};
    for (const k in ranges) ind[k] = sampleParam(ranges[k], _);
    return ind;
  }
  function clampParam(spec, v) {
    if (Array.isArray(spec) && typeof spec[0] === 'string') return v;
    const [lo, hi, type = 'float'] = spec;
    let x = Math.max(lo, Math.min(hi, v));
    return type === 'int' ? Math.round(x) : x;
  }

  // ────────────────────────────────────────────────────────────────────
  // GA operators
  // ────────────────────────────────────────────────────────────────────
  function tournamentSelect(pop, fitness, k, _) {
    let best = null;
    for (let i = 0; i < k; i++) {
      const idx = Math.floor(_() * pop.length);
      if (best === null || fitness[idx] > fitness[best]) best = idx;
    }
    return pop[best];
  }
  function crossover(p1, p2, _) {
    const child = {};
    for (const k in p1) child[k] = _() < 0.5 ? p1[k] : p2[k];
    return child;
  }
  function mutate(ind, ranges, _, norm, rate = 0.15, sigma = 0.15) {
    const out = {};
    for (const k in ind) {
      const spec = ranges[k];
      if (_() < rate) {
        if (Array.isArray(spec) && typeof spec[0] === 'string') {
          out[k] = spec[Math.floor(_() * spec.length)];
        } else {
          const [lo, hi] = spec;
          const span = hi - lo;
          out[k] = clampParam(spec, ind[k] + norm() * sigma * span);
        }
      } else {
        out[k] = ind[k];
      }
    }
    return out;
  }

  // ────────────────────────────────────────────────────────────────────
  // Single-objective GA
  // ────────────────────────────────────────────────────────────────────
  async function geneticAlgorithm({
    ranges, fitness, generations = 40, populationSize = 50,
    seed = 42, eliteFrac = 0.10, tournamentK = 3,
    mutationRate = 0.15, mutationSigma = 0.15, onGeneration = null,
  }) {
    const _ = rng(seed), norm = gauss(_);
    let pop = Array.from({ length: populationSize }, () => sampleIndividual(ranges, _));
    let fit = await Promise.all(pop.map(fitness));
    const history = [];
    const eliteN = Math.max(1, Math.floor(populationSize * eliteFrac));

    for (let gen = 0; gen < generations; gen++) {
      // Sort by fitness desc
      const idx = pop.map((_p, i) => i).sort((a, b) => fit[b] - fit[a]);
      const sortedPop = idx.map(i => pop[i]);
      const sortedFit = idx.map(i => fit[i]);

      // Stats
      const bestFitness = sortedFit[0];
      const avgFitness = sortedFit.reduce((s, v) => s + v, 0) / sortedFit.length;
      const diversity  = stats ? stats.stdev(sortedFit) : 0;
      history.push({ gen, best: bestFitness, avg: avgFitness, diversity });

      // Notify
      if (onGeneration) onGeneration({
        gen, best: bestFitness, avg: avgFitness, diversity,
        bestIndividual: sortedPop[0], population: sortedPop, fitness: sortedFit,
      });

      // Next generation: elites + offspring
      const next = sortedPop.slice(0, eliteN);
      while (next.length < populationSize) {
        const p1 = tournamentSelect(sortedPop, sortedFit, tournamentK, _);
        const p2 = tournamentSelect(sortedPop, sortedFit, tournamentK, _);
        let child = crossover(p1, p2, _);
        child = mutate(child, ranges, _, norm, mutationRate, mutationSigma);
        next.push(child);
      }
      pop = next;
      fit = await Promise.all(pop.map(fitness));
    }
    const finalIdx = pop.map((_p, i) => i).sort((a, b) => fit[b] - fit[a]);
    return {
      bestParams: pop[finalIdx[0]],
      bestFitness: fit[finalIdx[0]],
      history,
      finalPopulation: finalIdx.map(i => ({ params: pop[i], fitness: fit[i] })),
    };
  }

  // ────────────────────────────────────────────────────────────────────
  // NSGA-II — multi-objective non-dominated sorting + crowding distance
  // Objectives spec: ['max','min','min'] etc, same order as fitness obj
  // ────────────────────────────────────────────────────────────────────
  function dominates(a, b, dirs) {
    let strictly = false;
    for (let i = 0; i < a.length; i++) {
      const av = dirs[i] === 'max' ? a[i] : -a[i];
      const bv = dirs[i] === 'max' ? b[i] : -b[i];
      if (av < bv) return false;
      if (av > bv) strictly = true;
    }
    return strictly;
  }
  function fastNonDominatedSort(vecs, dirs) {
    const N = vecs.length;
    const S = Array.from({ length: N }, () => []);
    const n = new Array(N).fill(0);
    const ranks = new Array(N).fill(-1);
    const fronts = [[]];
    for (let p = 0; p < N; p++) {
      for (let q = 0; q < N; q++) {
        if (p === q) continue;
        if (dominates(vecs[p], vecs[q], dirs)) S[p].push(q);
        else if (dominates(vecs[q], vecs[p], dirs)) n[p]++;
      }
      if (n[p] === 0) { ranks[p] = 0; fronts[0].push(p); }
    }
    let i = 0;
    while (fronts[i].length) {
      const next = [];
      for (const p of fronts[i]) {
        for (const q of S[p]) {
          n[q]--;
          if (n[q] === 0) { ranks[q] = i + 1; next.push(q); }
        }
      }
      i++; fronts.push(next);
    }
    fronts.pop();
    return { fronts, ranks };
  }
  function crowdingDistance(front, vecs) {
    const N = front.length;
    if (N === 0) return [];
    const dist = new Array(N).fill(0);
    const M = vecs[0].length;
    for (let m = 0; m < M; m++) {
      const order = [...Array(N).keys()].sort((a, b) => vecs[front[a]][m] - vecs[front[b]][m]);
      dist[order[0]] = Infinity; dist[order[N - 1]] = Infinity;
      const vmin = vecs[front[order[0]]][m], vmax = vecs[front[order[N - 1]]][m];
      const range = vmax - vmin || 1;
      for (let i = 1; i < N - 1; i++) {
        dist[order[i]] += (vecs[front[order[i + 1]]][m] - vecs[front[order[i - 1]]][m]) / range;
      }
    }
    return dist;
  }
  async function nsga2({
    ranges, fitnessMulti, objectives, // objectives:['max'|'min', ...]
    generations = 40, populationSize = 60, seed = 42,
    mutationRate = 0.15, mutationSigma = 0.15, onGeneration = null,
  }) {
    const _ = rng(seed), norm = gauss(_);
    let pop = Array.from({ length: populationSize }, () => sampleIndividual(ranges, _));
    let vecs = await Promise.all(pop.map(fitnessMulti));

    for (let gen = 0; gen < generations; gen++) {
      // Generate offspring
      const offspring = [];
      while (offspring.length < populationSize) {
        const p1 = pop[Math.floor(_() * pop.length)];
        const p2 = pop[Math.floor(_() * pop.length)];
        let child = crossover(p1, p2, _);
        child = mutate(child, ranges, _, norm, mutationRate, mutationSigma);
        offspring.push(child);
      }
      const offspringVecs = await Promise.all(offspring.map(fitnessMulti));
      const combined = pop.concat(offspring);
      const combinedVecs = vecs.concat(offspringVecs);

      // Non-dominated sort
      const { fronts } = fastNonDominatedSort(combinedVecs, objectives);
      const nextIdx = [];
      for (const front of fronts) {
        if (nextIdx.length + front.length <= populationSize) {
          nextIdx.push(...front);
        } else {
          const remaining = populationSize - nextIdx.length;
          const cd = crowdingDistance(front, combinedVecs);
          const order = front.map((idx, i) => ({ idx, cd: cd[i] }))
                             .sort((a, b) => b.cd - a.cd);
          nextIdx.push(...order.slice(0, remaining).map(o => o.idx));
          break;
        }
      }
      pop = nextIdx.map(i => combined[i]);
      vecs = nextIdx.map(i => combinedVecs[i]);

      if (onGeneration) {
        const front0 = fronts[0].map(i => ({ params: combined[i], objectives: combinedVecs[i] }));
        onGeneration({ gen, paretoFront: front0, population: pop, objectives: vecs });
      }
    }

    // Final Pareto front
    const { fronts } = fastNonDominatedSort(vecs, objectives);
    const front0 = fronts[0].map(i => ({ params: pop[i], objectives: vecs[i] }));
    return { paretoFront: front0, finalPopulation: pop.map((p, i) => ({ params: p, objectives: vecs[i] })) };
  }

  // ────────────────────────────────────────────────────────────────────
  // Bayesian optimisation — Gaussian-process surrogate w/ EI acquisition
  // Continuous params only; categoricals fall back to random sampling
  // ────────────────────────────────────────────────────────────────────
  function rbfKernel(x1, x2, lengthScale, variance) {
    let d = 0;
    for (let i = 0; i < x1.length; i++) {
      const r = (x1[i] - x2[i]) / lengthScale;
      d += r * r;
    }
    return variance * Math.exp(-0.5 * d);
  }
  function buildKernelMatrix(X, ls, v, noise) {
    const N = X.length;
    const K = Array.from({ length: N }, () => new Array(N).fill(0));
    for (let i = 0; i < N; i++) {
      for (let j = i; j < N; j++) {
        const k = rbfKernel(X[i], X[j], ls, v);
        K[i][j] = K[j][i] = k + (i === j ? noise : 0);
      }
    }
    return K;
  }
  function choleskyDecompose(A) {
    const N = A.length;
    const L = Array.from({ length: N }, () => new Array(N).fill(0));
    for (let i = 0; i < N; i++) {
      for (let j = 0; j <= i; j++) {
        let sum = 0;
        for (let k = 0; k < j; k++) sum += L[i][k] * L[j][k];
        if (i === j) L[i][j] = Math.sqrt(Math.max(1e-9, A[i][i] - sum));
        else         L[i][j] = (A[i][j] - sum) / L[j][j];
      }
    }
    return L;
  }
  function choleskySolve(L, b) {
    const N = L.length;
    const y = new Array(N).fill(0), x = new Array(N).fill(0);
    for (let i = 0; i < N; i++) {
      let s = b[i];
      for (let k = 0; k < i; k++) s -= L[i][k] * y[k];
      y[i] = s / L[i][i];
    }
    for (let i = N - 1; i >= 0; i--) {
      let s = y[i];
      for (let k = i + 1; k < N; k++) s -= L[k][i] * x[k];
      x[i] = s / L[i][i];
    }
    return x;
  }
  function gpPredict(X, y, ls, v, noise, xstar) {
    const K = buildKernelMatrix(X, ls, v, noise);
    const L = choleskyDecompose(K);
    const alpha = choleskySolve(L, y);
    const kstar = X.map(x => rbfKernel(x, xstar, ls, v));
    const mean = kstar.reduce((s, k, i) => s + k * alpha[i], 0);
    // Variance ≈ k(x,x) - kstar' K^-1 kstar (skip exact compute, approximate)
    const kss = rbfKernel(xstar, xstar, ls, v);
    const beta = choleskySolve(L, kstar);
    const variance = Math.max(1e-6, kss - kstar.reduce((s, k, i) => s + k * beta[i], 0));
    return { mean, std: Math.sqrt(variance) };
  }
  function expectedImprovement(mean, std, fbest) {
    if (std < 1e-8) return 0;
    const z = (mean - fbest) / std;
    const norm = (x) => (1 / Math.sqrt(2 * Math.PI)) * Math.exp(-0.5 * x * x);
    const cdf = stats ? stats.normCDF(z) : 0.5 * (1 + erf(z / Math.SQRT2));
    return (mean - fbest) * cdf + std * norm(z);
  }
  function erf(x) {
    const a1 = 0.254829592, a2 = -0.284496736, a3 = 1.421413741;
    const a4 = -1.453152027, a5 = 1.061405429, p = 0.3275911;
    const sign = x < 0 ? -1 : 1;
    x = Math.abs(x);
    const t = 1 / (1 + p * x);
    return sign * (1 - (((((a5 * t + a4) * t) + a3) * t + a2) * t + a1) * t * Math.exp(-x * x));
  }

  async function bayesianOptimize({
    ranges, fitness, iterations = 40, initialPoints = 8, seed = 42,
    onIteration = null,
  }) {
    const _ = rng(seed);
    const paramKeys = Object.keys(ranges);
    const isContinuous = (k) => !(Array.isArray(ranges[k]) && typeof ranges[k][0] === 'string');
    const contKeys = paramKeys.filter(isContinuous);

    // Normalise continuous params to [0,1]; categoricals random-sample
    function normalize(p) {
      return contKeys.map(k => {
        const [lo, hi] = ranges[k];
        return (p[k] - lo) / (hi - lo);
      });
    }
    function denormalize(v) {
      const p = {};
      contKeys.forEach((k, i) => {
        const [lo, hi, type = 'float'] = ranges[k];
        let x = lo + v[i] * (hi - lo);
        p[k] = type === 'int' ? Math.round(x) : x;
      });
      // Categoricals: random
      paramKeys.filter(k => !isContinuous(k)).forEach(k => {
        p[k] = ranges[k][Math.floor(_() * ranges[k].length)];
      });
      return p;
    }

    // Seed with random points
    const X = [], y = [], paramsLog = [];
    for (let i = 0; i < initialPoints; i++) {
      const p = sampleIndividual(ranges, _);
      const f = await fitness(p);
      X.push(normalize(p)); y.push(f);
      paramsLog.push({ params: p, fitness: f });
    }

    for (let it = 0; it < iterations; it++) {
      const fbest = Math.max(...y);
      // Sample candidates, pick one with highest EI
      let bestEI = -Infinity, bestCand = null;
      for (let c = 0; c < 200; c++) {
        const v = contKeys.map(() => _());
        const { mean, std } = gpPredict(X, y, 0.3, 1.0, 1e-3, v);
        const ei = expectedImprovement(mean, std, fbest);
        if (ei > bestEI) { bestEI = ei; bestCand = v; }
      }
      const p = denormalize(bestCand);
      const f = await fitness(p);
      X.push(bestCand); y.push(f);
      paramsLog.push({ params: p, fitness: f });
      if (onIteration) onIteration({ iter: it, candidate: p, fitness: f, ei: bestEI });
    }
    const bestIdx = y.indexOf(Math.max(...y));
    return { bestParams: paramsLog[bestIdx].params, bestFitness: y[bestIdx], history: paramsLog };
  }

  // ────────────────────────────────────────────────────────────────────
  // Grid + random search
  // ────────────────────────────────────────────────────────────────────
  async function gridSearch({ ranges, fitness, gridSize = 5 }) {
    const keys = Object.keys(ranges);
    const grids = keys.map(k => {
      const spec = ranges[k];
      if (Array.isArray(spec) && typeof spec[0] === 'string') return spec;
      const [lo, hi, type = 'float'] = spec;
      const arr = [];
      for (let i = 0; i < gridSize; i++) {
        const v = lo + (i / (gridSize - 1)) * (hi - lo);
        arr.push(type === 'int' ? Math.round(v) : v);
      }
      return arr;
    });
    const results = [];
    async function recur(idx, cur) {
      if (idx === keys.length) {
        const f = await fitness(cur);
        results.push({ params: { ...cur }, fitness: f });
        return;
      }
      for (const v of grids[idx]) await recur(idx + 1, { ...cur, [keys[idx]]: v });
    }
    await recur(0, {});
    results.sort((a, b) => b.fitness - a.fitness);
    return { bestParams: results[0].params, bestFitness: results[0].fitness, all: results };
  }
  async function randomSearch({ ranges, fitness, iterations = 100, seed = 42 }) {
    const _ = rng(seed);
    const results = [];
    for (let i = 0; i < iterations; i++) {
      const p = sampleIndividual(ranges, _);
      const f = await fitness(p);
      results.push({ params: p, fitness: f });
    }
    results.sort((a, b) => b.fitness - a.fitness);
    return { bestParams: results[0].params, bestFitness: results[0].fitness, all: results };
  }

  // ────────────────────────────────────────────────────────────────────
  // Walk-forward optimisation
  // ────────────────────────────────────────────────────────────────────
  async function walkForwardOptimize({
    ranges, fitnessFor, // fitnessFor(slice) returns a fitness fn for that slice
    folds = 5, trainPct = 0.7, totalBars,
    optimizer = geneticAlgorithm, optimizerOpts = {},
  }) {
    const foldSize = Math.floor(totalBars / folds);
    const foldResults = [];
    const allBest = [];
    for (let f = 0; f < folds; f++) {
      const s = f * foldSize, e = Math.min(totalBars - 1, s + foldSize);
      const split = Math.floor(s + (e - s) * trainPct);
      const trainFit = fitnessFor([s, split]);
      const testFit  = fitnessFor([split + 1, e]);
      const { bestParams, bestFitness } = await optimizer({
        ...optimizerOpts, ranges, fitness: trainFit,
      });
      const outFitness = await testFit(bestParams);
      foldResults.push({
        fold: f, range: [s, e], split,
        bestParams, trainFitness: bestFitness, testFitness: outFitness,
      });
      allBest.push(bestParams);
    }
    // Parameter stability: variance of each param across folds
    const stability = {};
    Object.keys(ranges).forEach(k => {
      const vals = allBest.map(b => typeof b[k] === 'number' ? b[k] : 0);
      stability[k] = stats ? stats.stdev(vals) / (Math.abs(stats.mean(vals)) + 1e-9) : 0;
    });
    return {
      folds: foldResults,
      paramStability: stability,
      meanTrain: stats.mean(foldResults.map(r => r.trainFitness)),
      meanTest:  stats.mean(foldResults.map(r => r.testFitness)),
    };
  }

  // ────────────────────────────────────────────────────────────────────
  // PBO — Probability of Backtest Overfitting (López de Prado)
  // performanceMatrix: NxT matrix where rows = strategies (param sets),
  // cols = sub-period sharpes. Returns probability that the best strategy
  // in-sample ranks below median out-of-sample.
  // ────────────────────────────────────────────────────────────────────
  function pbo(performanceMatrix, S = 16) {
    const N = performanceMatrix.length;       // strategies
    const T = performanceMatrix[0].length;    // time slices
    if (S > T) S = T;                          // can't split more than columns

    // Generate combinatorial splits
    const indices = [...Array(T).keys()];
    const splits = [];
    // We enumerate up to 50 random partitions for speed
    for (let s = 0; s < 50; s++) {
      const shuffled = indices.slice();
      for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
      }
      const half = Math.floor(T / 2);
      splits.push({ inSample: shuffled.slice(0, half), outSample: shuffled.slice(half) });
    }
    let overfitCount = 0;
    for (const split of splits) {
      // Best strategy in-sample
      let bestIdx = 0, bestVal = -Infinity;
      for (let i = 0; i < N; i++) {
        const v = stats.mean(split.inSample.map(t => performanceMatrix[i][t]));
        if (v > bestVal) { bestVal = v; bestIdx = i; }
      }
      // Out-of-sample rank of that strategy
      const oosVals = [];
      for (let i = 0; i < N; i++) {
        oosVals.push({ idx: i, val: stats.mean(split.outSample.map(t => performanceMatrix[i][t])) });
      }
      oosVals.sort((a, b) => b.val - a.val);
      const rank = oosVals.findIndex(o => o.idx === bestIdx) / (N - 1);
      if (rank > 0.5) overfitCount++; // best in-sample fell to below-median OOS
    }
    return overfitCount / splits.length;
  }

  // ────────────────────────────────────────────────────────────────────
  // Sensitivity analysis — re-evaluate at ±5/10/20% per param
  // ────────────────────────────────────────────────────────────────────
  async function sensitivityAnalysis({ baseParams, ranges, fitness, perturbations = [-0.20, -0.10, -0.05, 0.05, 0.10, 0.20] }) {
    const baseFitness = await fitness(baseParams);
    const out = {};
    for (const key in baseParams) {
      const spec = ranges[key];
      if (!spec || (Array.isArray(spec) && typeof spec[0] === 'string')) continue;
      out[key] = [];
      for (const pct of perturbations) {
        const v = clampParam(spec, baseParams[key] * (1 + pct));
        const f = await fitness({ ...baseParams, [key]: v });
        out[key].push({ pct, value: v, fitness: f, delta: f - baseFitness });
      }
    }
    return { baseFitness, sensitivity: out };
  }

  // ────────────────────────────────────────────────────────────────────
  // Neighbourhood robustness — variance of nearby fitnesses
  // ────────────────────────────────────────────────────────────────────
  async function robustnessScore({ baseParams, ranges, fitness, samples = 20, neighborhood = 0.10, seed = 7 }) {
    const _ = rng(seed), norm = gauss(_);
    const fitnesses = [];
    const baseFitness = await fitness(baseParams);
    for (let i = 0; i < samples; i++) {
      const cand = {};
      for (const k in baseParams) {
        const spec = ranges[k];
        if (!spec) { cand[k] = baseParams[k]; continue; }
        if (Array.isArray(spec) && typeof spec[0] === 'string') { cand[k] = baseParams[k]; continue; }
        const [lo, hi] = spec;
        const span = hi - lo;
        cand[k] = clampParam(spec, baseParams[k] + norm() * neighborhood * span);
      }
      fitnesses.push(await fitness(cand));
    }
    const mean = stats.mean(fitnesses);
    const std  = stats.stdev(fitnesses);
    // Score 0-100: higher = more robust (low variance, close to base fitness)
    const cv = Math.abs(mean) > 1e-9 ? std / Math.abs(mean) : 1;
    const proximity = 1 - Math.abs(baseFitness - mean) / (Math.abs(baseFitness) + 1e-9);
    const score = Math.round(100 * Math.max(0, Math.min(1, (1 - cv) * 0.6 + proximity * 0.4)));
    return { score, baseFitness, neighbourMean: mean, neighbourStd: std, samples: fitnesses };
  }

  const api = {
    geneticAlgorithm, nsga2, bayesianOptimize, gridSearch, randomSearch,
    walkForwardOptimize, pbo, sensitivityAnalysis, robustnessScore,
    fastNonDominatedSort, crowdingDistance, dominates,
    sampleIndividual, sampleParam, rng,
  };
  if (root) { root.GQ = root.GQ || {}; root.GQ.optimize = api; }
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
})(typeof window !== 'undefined' ? window : globalThis);
